// src/components/dashboard/QuickBookingWidget.js
import {
    Calendar,
    Check,
    Clock,
    Loader,
    User,
    X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useBookings } from '../../hooks/useBookings';
import { useServices } from '../../hooks/useServices';

const QuickBookingWidget = ({ isOpen, onClose, onSuccess, preselectedDate = null }) => {
  const { user } = useAuth();
  const { services } = useServices();
  const { createBooking } = useBookings();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    serviceName: '',
    servicePrice: '',
    duration: '60',
    bookingDate: preselectedDate || getTodayDate(),
    time: '',
    notes: ''
  });

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setError(null);
      setSuccess(false);
      setFormData(prev => ({
        ...prev,
        bookingDate: preselectedDate || getTodayDate()
      }));
    }
  }, [isOpen, preselectedDate]);

  // Get suggested times based on business hours
  const getSuggestedTimes = () => {
    const times = [];
    // Generate time slots from 9 AM to 6 PM (can be customized)
    for (let hour = 9; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push(timeString);
      }
    }
    return times;
  };

  const handleServiceChange = (serviceName) => {
    const service = services.find(s => s.name === serviceName);
    setFormData(prev => ({
      ...prev,
      serviceName,
      servicePrice: service?.price || '',
      duration: service?.duration || '60'
    }));
  };

  const handleNextStep = () => {
    setError(null);
    
    // Validate current step
    if (step === 1) {
      if (!formData.customerName || !formData.customerPhone) {
        setError('Customer name and phone are required');
        return;
      }
    } else if (step === 2) {
      if (!formData.serviceName) {
        setError('Please select a service');
        return;
      }
    } else if (step === 3) {
      if (!formData.bookingDate || !formData.time) {
        setError('Please select date and time');
        return;
      }
    }
    
    setStep(step + 1);
  };

  const handlePreviousStep = () => {
    setError(null);
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const bookingData = {
        ...formData,
        notify_customer: notifyCustomer
      };

      const result = await createBooking(bookingData);
      
      if (result.success) {
        setSuccess(true);
        onSuccess?.(result.booking);
        
        // Auto-close after success
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      serviceName: '',
      servicePrice: '',
      duration: '60',
      bookingDate: getTodayDate(),
      time: '',
      notes: ''
    });
    setStep(1);
    setError(null);
    setSuccess(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Quick Booking</h2>
              <p className="text-sm text-gray-600">Step {step} of 4</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`flex-1 h-2 rounded-full ${
                    i <= step ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <SuccessStep />
          ) : (
            <>
              {step === 1 && (
                <CustomerStep
                  formData={formData}
                  setFormData={setFormData}
                  onNext={handleNextStep}
                  error={error}
                />
              )}

              {step === 2 && (
                <ServiceStep
                  formData={formData}
                  services={services}
                  onServiceChange={handleServiceChange}
                  onNext={handleNextStep}
                  onPrevious={handlePreviousStep}
                  error={error}
                />
              )}

              {step === 3 && (
                <DateTimeStep
                  formData={formData}
                  setFormData={setFormData}
                  suggestedTimes={getSuggestedTimes()}
                  onNext={handleNextStep}
                  onPrevious={handlePreviousStep}
                  error={error}
                />
              )}

              {step === 4 && (
                <ConfirmStep
                  formData={formData}
                  setFormData={setFormData}
                  notifyCustomer={notifyCustomer}
                  setNotifyCustomer={setNotifyCustomer}
                  onConfirm={handleSubmit}
                  onPrevious={handlePreviousStep}
                  loading={loading}
                  error={error}
                />
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        {!success && (
          <div className="p-6 border-t border-gray-200">
            <div className="flex justify-between">
              <button
                onClick={resetForm}
                className="text-gray-600 hover:text-gray-800 text-sm"
              >
                Reset Form
              </button>
              
              <div className="text-xs text-gray-500">
                Quick booking for immediate appointments
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Step Components
const CustomerStep = ({ formData, setFormData, onNext, error }) => (
  <div className="space-y-4">
    <div className="text-center mb-6">
      <User className="h-12 w-12 text-blue-600 mx-auto mb-2" />
      <h3 className="text-lg font-medium text-gray-900">Customer Information</h3>
      <p className="text-gray-600">Who is this booking for?</p>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Customer Name *
      </label>
      <input
        type="text"
        required
        value={formData.customerName}
        onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter customer name"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Phone Number *
      </label>
      <input
        type="tel"
        required
        value={formData.customerPhone}
        onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter phone number"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Email (Optional)
      </label>
      <input
        type="email"
        value={formData.customerEmail}
        onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter email address"
      />
    </div>

    {error && (
      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
        {error}
      </div>
    )}

    <button
      onClick={onNext}
      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center"
    >
      Next: Choose Service
      <Calendar className="h-4 w-4 ml-2" />
    </button>
  </div>
);

const ServiceStep = ({ formData, services, onServiceChange, onNext, onPrevious, error }) => (
  <div className="space-y-4">
    <div className="text-center mb-6">
      
      <h3 className="text-lg font-medium text-gray-900">Select Service</h3>
      <p className="text-gray-600">What service would you like to book?</p>
    </div>

    <div className="space-y-3">
      {services.map((service) => (
        <div
          key={service.id || service.name}
          onClick={() => onServiceChange(service.name)}
          className={`
            border rounded-lg p-4 cursor-pointer transition-colors
            ${formData.serviceName === service.name
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
            }
          `}
        >
          <div className="flex justify-between items-center">
            <div>
              <h4 className="font-medium text-gray-900">{service.name}</h4>
              <p className="text-sm text-gray-600">{service.duration} minutes</p>
            </div>
            <div className="text-right">
              <p className="font-medium text-gray-900">${service.price}</p>
            </div>
          </div>
        </div>
      ))}
    </div>

    {error && (
      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
        {error}
      </div>
    )}

    <div className="flex space-x-3">
      <button
        onClick={onPrevious}
        className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
      >
        Previous
      </button>
      <button
        onClick={onNext}
        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center"
      >
        Next: Date & Time
        <Clock className="h-4 w-4 ml-2" />
      </button>
    </div>
  </div>
);

const DateTimeStep = ({ formData, setFormData, suggestedTimes, onNext, onPrevious, error }) => (
  <div className="space-y-4">
    <div className="text-center mb-6">
      <Clock className="h-12 w-12 text-blue-600 mx-auto mb-2" />
      <h3 className="text-lg font-medium text-gray-900">Select Date & Time</h3>
      <p className="text-gray-600">When would you like to schedule this?</p>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Date *
      </label>
      <input
        type="date"
        required
        value={formData.bookingDate}
        min={getTodayDate()}
        onChange={(e) => setFormData(prev => ({ ...prev, bookingDate: e.target.value }))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Time *
      </label>
      <div className="grid grid-cols-3 gap-2">
        {suggestedTimes.slice(0, 12).map((time) => (
          <button
            key={time}
            onClick={() => setFormData(prev => ({ ...prev, time }))}
            className={`
              py-2 px-3 text-sm rounded-lg border transition-colors
              ${formData.time === time
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
              }
            `}
          >
            {formatTime(time)}
          </button>
        ))}
      </div>
      
      <div className="mt-2">
        <input
          type="time"
          value={formData.time}
          onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>

    {error && (
      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
        {error}
      </div>
    )}

    <div className="flex space-x-3">
      <button
        onClick={onPrevious}
        className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
      >
        Previous
      </button>
      <button
        onClick={onNext}
        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center"
      >
        Review Booking
        <Check className="h-4 w-4 ml-2" />
      </button>
    </div>
  </div>
);

const ConfirmStep = ({
  formData,
  setFormData,
  notifyCustomer,
  setNotifyCustomer,
  onConfirm,
  onPrevious,
  loading,
  error
}) => (
  <div className="space-y-4">
    <div className="text-center mb-6">
      <Check className="h-12 w-12 text-green-600 mx-auto mb-2" />
      <h3 className="text-lg font-medium text-gray-900">Confirm Booking</h3>
      <p className="text-gray-600">Please review the booking details</p>
    </div>

    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div>
        <label className="text-sm text-gray-600">Customer</label>
        <p className="font-medium">{formData.customerName}</p>
        <p className="text-sm text-gray-600">{formData.customerPhone}</p>
      </div>
      
      <div>
        <label className="text-sm text-gray-600">Service</label>
        <p className="font-medium">{formData.serviceName}</p>
        <p className="text-sm text-gray-600">${formData.servicePrice} • {formData.duration} minutes</p>
      </div>
      
      <div>
        <label className="text-sm text-gray-600">Date & Time</label>
        <p className="font-medium">
          {new Date(formData.bookingDate).toLocaleDateString()} at {formatTime(formData.time)}
        </p>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Notes (Optional)
      </label>
      <textarea
        value={formData.notes}
        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
        rows="3"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        placeholder="Add any special notes or requirements..."
      />
    </div>

    {error && (
      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
        {error}
      </div>
    )}

    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
      <input
        type="checkbox"
        id="qbw-notifyCustomer"
        checked={notifyCustomer}
        onChange={(e) => setNotifyCustomer(e.target.checked)}
      />
      <label htmlFor="qbw-notifyCustomer" style={{ fontSize: '14px', cursor: 'pointer' }}>
        Send WhatsApp confirmation to customer
      </label>
    </div>

    <div className="flex space-x-3">
      <button
        onClick={onPrevious}
        disabled={loading}
        className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 disabled:opacity-50"
      >
        Previous
      </button>
      <button
        onClick={onConfirm}
        disabled={loading}
        className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
      >
        {loading ? (
          <>
            <Loader className="h-4 w-4 animate-spin mr-2" />
            Creating...
          </>
        ) : (
          <>
            <Check className="h-4 w-4 mr-2" />
            Confirm Booking
          </>
        )}
      </button>
    </div>
  </div>
);

const SuccessStep = () => (
  <div className="text-center py-8">
    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Check className="h-8 w-8 text-green-600" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">Booking Created Successfully!</h3>
    <p className="text-gray-600 mb-4">The customer will receive a confirmation notification.</p>
    <div className="animate-pulse">
      <div className="h-2 bg-green-200 rounded-full"></div>
    </div>
  </div>
);

// Helper functions
function getTodayDate() {
  return new Date().toISOString().split('T')[0];
}

function formatTime(time) {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

export default QuickBookingWidget;
